import { Job, JobCreateRequest, JobUpdateRequest,PaginatedResult } from '../../types/types';
import { apiClient } from './apiClient';

/**
 * Service for managing jobs in the HR Tracker system
 */
class JobService {
    private baseUrl = '/job';

    /**
     * Get all jobs
     * @returns Promise with array of jobs
     */
    async getJobs(): Promise<PaginatedResult<Job>> {
        try {
            const response = await apiClient.get(this.baseUrl);
            return response.data;
        } catch (error) {
            console.error('Error fetching jobs:', error);
            throw error;
        }
    }

    /**
     * Get a specific job by ID
     * @param id Job ID
     * @returns Promise with job data
     */
    async getJobById(id: string): Promise<Job> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching job with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Create a new job
     * @param jobData Job data to create
     * @returns Promise with created job data
     */
    async createJob(jobData: JobCreateRequest): Promise<Job> {
        try {
            const response = await apiClient.post(this.baseUrl, jobData);
            return response.data;
        } catch (error) {
            console.error('Error creating job:', error);
            throw error;
        }
    }

    /**
     * Update an existing job
     * @param id Job ID
     * @param jobData Updated job data
     * @returns Promise with updated job data
     */
    async updateJob(id: string, jobData: JobUpdateRequest): Promise<Job> {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${id}`, jobData);
            return response.data;
        } catch (error) {
            console.error(`Error updating job with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Delete a job
     * @param id Job ID to delete
     * @returns Promise with deletion confirmation
     */
    async deleteJob(id: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/${id}`);
        } catch (error) {
            console.error(`Error deleting job with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get all active jobs (for dropdown lists, etc.)
     * @returns Promise with array of active jobs
     */
    async getActiveJobs(): Promise<Job[]> {
        try {
            const response = await apiClient.get(`${this.baseUrl}/active`);
            return response.data;
        } catch (error) {
            console.error('Error fetching active jobs:', error);
            throw error;
        }
    }
}

export const jobService = new JobService();